import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/hooks/use-toast';

export interface SubscriptionPlan {
  id: string;
  code: string;
  name: string;
  description: string;
  price_monthly_cents: number;
  price_yearly_cents: number;
  max_users: number;
  max_facilities: number;
  max_animals: number;
  stripe_product_id: string;
  stripe_price_monthly_id: string;
  stripe_price_yearly_id: string;
}

export interface AddonType {
  id: string;
  code: string;
  name: string;
  description: string;
  price_monthly_cents: number;
  unit_amount: number;
  unit_type: string;
  stripe_product_id: string;
  stripe_price_id: string;
}

export interface SubscriptionStatus {
  hasActiveSubscription: boolean;
  subscription?: {
    id: string;
    planName: string;
    planCode: string;
    status: string;
    billingCycle: string;
    currentPeriodEnd: string;
    cancelAtPeriodEnd: boolean;
    trialEnd?: string;
    isInTrial?: boolean;
    trialDaysRemaining?: number;
  };
  currentUsage: {
    users: number;
    facilities: number;
    animals: number;
    archivedAnimals: number;
  };
  totalLimits: {
    users: number;
    facilities: number;
    animals: number;
    archivedAnimals: number;
  };
  usagePercentages: {
    users: number;
    facilities: number;
    animals: number;
    archivedAnimals: number;
  };
  addons: Array<{
    id: string;
    name: string;
    quantity: number;
    unitType: string;
    unitAmount: number;
  }>;
  alerts: {
    softLimitWarnings: Array<{
      resource: string;
      current: number;
      limit: number;
      percentage: number;
    }>;
    hardLimitBlocks: Array<{
      resource: string;
      current: number;
      limit: number;
      percentage: number;
    }>;
  };
}

export const useSubscription = () => {
  const { session, isAuthenticated } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch subscription plans
  const { data: plans, isLoading: plansLoading } = useQuery({
    queryKey: ['subscription-plans'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('subscription_plans')
        .select('*')
        .eq('is_active', true)
        .order('price_monthly_cents');
      
      if (error) throw error;
      return data as SubscriptionPlan[];
    },
    enabled: isAuthenticated
  });

  // Fetch addon types
  const { data: addonTypes, isLoading: addonTypesLoading } = useQuery({
    queryKey: ['addon-types'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('addon_types')
        .select('*')
        .eq('is_active', true)
        .order('price_monthly_cents');
      
      if (error) throw error;
      return data as AddonType[];
    },
    enabled: isAuthenticated
  });

  // Check subscription status
  const { 
    data: subscriptionStatus, 
    isLoading: statusLoading,
    refetch: refetchStatus
  } = useQuery({
    queryKey: ['subscription-status'],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke('check-subscription-status', {
        headers: {
          Authorization: `Bearer ${session?.access_token}`,
        },
      });

      if (error) throw error;
      return data as SubscriptionStatus;
    },
    enabled: isAuthenticated && !!session,
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
  });

  // Create subscription checkout
  const createCheckoutMutation = useMutation({
    mutationFn: async ({ planCode, billingCycle, couponCode }: { 
      planCode: string; 
      billingCycle: 'monthly' | 'yearly';
      couponCode?: string;
    }) => {
      if (import.meta.env.DEV) console.log('Creating checkout for:', { planCode, billingCycle, couponCode });
      
      const { data, error } = await supabase.functions.invoke('create-subscription-checkout', {
        body: { planCode, billingCycle, couponCode },
        headers: {
          Authorization: `Bearer ${session?.access_token}`,
        },
      });

      if (import.meta.env.DEV) console.log('Checkout response:', { data, error });

      if (error) {
        if (import.meta.env.DEV) console.error('Checkout error details:', error);
        throw new Error(`Checkout failed: ${error.message || 'Unknown error'}`);
      }
      
      if (!data) {
        throw new Error('No response data received from checkout');
      }
      
      return data;
    },
    onSuccess: (data) => {
      if (import.meta.env.DEV) console.log('Checkout success:', data);
      
      // Handle already subscribed case
      if (data.alreadySubscribed) {
        toast({
          title: "Already Subscribed",
          description: data.message || "You already have an active subscription.",
        });
        if (data.portalUrl) {
          window.open(data.portalUrl, '_blank');
        }
        return;
      }
      
      if (data.url) {
        // Open Stripe checkout in new tab
        window.open(data.url, '_blank');
      } else {
        toast({
          title: "Checkout Error",
          description: "No checkout URL received",
          variant: "destructive",
        });
      }
    },
    onError: (error: any) => {
      if (import.meta.env.DEV) console.error('Checkout mutation error:', error);
      toast({
        title: "Checkout Error",
        description: error.message || "Failed to create checkout session",
        variant: "destructive",
      });
    },
  });

  // Manage addons
  const manageAddonMutation = useMutation({
    mutationFn: async ({ action, addonCode, quantity = 1 }: { 
      action: 'add' | 'remove'; 
      addonCode: string; 
      quantity?: number;
    }) => {
      const { data, error } = await supabase.functions.invoke('manage-addons', {
        body: { action, addonCode, quantity },
        headers: {
          Authorization: `Bearer ${session?.access_token}`,
        },
      });

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      toast({
        title: "Addon Updated",
        description: data.message,
        variant: "default",
      });
      // Refetch subscription status to get updated usage
      queryClient.invalidateQueries({ queryKey: ['subscription-status'] });
    },
    onError: (error: any) => {
      toast({
        title: "Addon Error",
        description: error.message || "Failed to manage addon",
        variant: "destructive",
      });
    },
  });

  // Cancel subscription
  const cancelSubscriptionMutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke('cancel-subscription', {
        headers: {
          Authorization: `Bearer ${session?.access_token}`,
        },
      });

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      toast({
        title: "Subscription Cancelled",
        description: data.message,
        variant: "default",
      });
      // Refetch subscription status to get updated cancellation info
      queryClient.invalidateQueries({ queryKey: ['subscription-status'] });
    },
    onError: (error: any) => {
      toast({
        title: "Cancellation Error",
        description: error.message || "Failed to cancel subscription",
        variant: "destructive",
      });
    },
  });

  // Open customer portal
  const openCustomerPortalMutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke('customer-portal', {
        headers: {
          Authorization: `Bearer ${session?.access_token}`,
        },
      });

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      if (data.url) {
        window.open(data.url, '_blank');
      }
    },
    onError: (error: any) => {
      toast({
        title: "Portal Error",
        description: error.message || "Failed to open customer portal",
        variant: "destructive",
      });
    },
  });

  // Check usage before creating resources
  const checkUsage = async (resourceType: 'users' | 'facilities' | 'animals', action: 'check' | 'create' = 'create') => {
    if (!session) return { canCreate: false, error: 'Not authenticated' };

    try {
      const { data, error } = await supabase.functions.invoke('usage-monitor', {
        body: { resourceType, action },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) throw error;
      return data;
    } catch (error: any) {
      return { canCreate: false, error: error.message };
    }
  };

  return {
    // Data
    plans,
    addonTypes,
    subscriptionStatus,
    
    // Loading states
    plansLoading,
    addonTypesLoading,
    statusLoading,
    
    // Actions
    createCheckout: createCheckoutMutation.mutate,
    createCheckoutLoading: createCheckoutMutation.isPending,
    
    manageAddon: manageAddonMutation.mutate,
    manageAddonLoading: manageAddonMutation.isPending,
    
    cancelSubscription: cancelSubscriptionMutation.mutate,
    cancelSubscriptionLoading: cancelSubscriptionMutation.isPending,
    
    openCustomerPortal: openCustomerPortalMutation.mutate,
    openCustomerPortalLoading: openCustomerPortalMutation.isPending,
    
    checkUsage,
    refetchStatus,
    
    // Helper functions
    formatPrice: (cents: number) => `$${(cents / 100).toFixed(2)}`,
    getYearlySavings: (monthlyPrice: number, yearlyPrice: number) => {
      const yearlySavings = (monthlyPrice * 12) - yearlyPrice;
      return Math.round((yearlySavings / (monthlyPrice * 12)) * 100);
    },
  };
};